import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined"
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import ListItemButton from "@mui/material/ListItemButton"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import {
  format,
  formatDistanceToNow,
  isThisYear,
  isToday,
  isYesterday,
} from "date-fns"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link as RouterLink, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { FOOTER_HEIGHT } from "../../../common/components/Footer"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import InboxChannelToggle from "./InboxChannelToggle"
import InboxContactChip from "./InboxContactChip"
import ProfileColumn from "./ProfileColumn"
import SmsInboxReplyComposer from "./SmsInboxReplyComposer"
import SmsInboxTemplatesDrawer from "./SmsInboxTemplatesDrawer"
import SmsMessageCard, { initials } from "./SmsMessageCard"
import SmsNewTextDialog from "./SmsNewTextDialog"
import { inboxContactDetailsPath } from "./inboxContactPath"

const SMS_INBOX_LIST_POLL_MS = 60000
const SMS_INBOX_THREAD_POLL_MS = 15000

function firstApiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") {
    return fallback
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail
  }
  for (const key of ["message", "phone", "non_field_errors", "detail"]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return value
    }
    if (Array.isArray(value) && value.length) {
      return value.map(String).join(" ")
    }
  }
  return fallback
}

function formatListTime(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  if (isToday(date)) return format(date, "h:mm a")
  if (isYesterday(date)) return "Yesterday"
  if (isThisYear(date)) return format(date, "MMM d")
  return format(date, "MMM d, yyyy")
}

function formatSyncLabel(iso) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`
}

/**
 * textingOnly: the page of a texting-only employee, who has no other inbox
 * channels, settings or contact pages to link to.
 */
export default function SmsInboxPanel({
  channel,
  onChannelChange,
  textingOnly = false,
}) {
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const [searchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const [mailbox, setMailbox] = useState(null)
  const [threads, setThreads] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [thread, setThread] = useState(null)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [newTextOpen, setNewTextOpen] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [templatesRevision, setTemplatesRevision] = useState(0)
  const threadScrollRef = useRef(null)
  const selectedIdRef = useRef(null)

  const concierge = isConcierge(profile)
  const threadFromUrl = searchParams.get("thread")
  const showList = !isMobile || !selectedId
  const showReadingPane = !isMobile || Boolean(selectedId)

  const loadThreads = useCallback(
    (options = {}) => {
      const silent = Boolean(options.silent)
      if (!silent) setIsLoading(true)
      return axios
        .get("/crm/api/concierge-sms/inbox/threads/")
        .then(({ data }) => {
          setMailbox(data.mailbox)
          setThreads(data.results || [])
          setError("")
        })
        .catch((err) => {
          setMailbox(null)
          setThreads([])
          setError(
            firstApiErrorMessage(
              err?.response?.data,
              "Could not load SMS inbox for this parent account."
            )
          )
        })
        .finally(() => {
          if (!silent) setIsLoading(false)
        })
    },
    [axios]
  )

  const loadThread = useCallback(
    (threadId, options = {}) => {
      const silent = Boolean(options.silent)
      if (!threadId) {
        setThread(null)
        return Promise.resolve()
      }
      return axios
        .get(`/crm/api/concierge-sms/inbox/threads/${threadId}/`)
        .then(({ data }) => {
          if (selectedIdRef.current !== threadId) return
          setThread(data)
          if (data.unread_count) {
            axios
              .post(`/crm/api/concierge-sms/inbox/threads/${threadId}/read/`)
              .then(({ data: updated }) => {
                if (selectedIdRef.current !== threadId) return
                setThread(updated)
                setThreads((current) =>
                  current.map((row) =>
                    row.thread_id === threadId
                      ? { ...row, unread_count: 0, is_read: true }
                      : row
                  )
                )
              })
              .catch(() => {})
          }
        })
        .catch((err) => {
          if (silent) return
          setError(
            firstApiErrorMessage(err?.response?.data, "Could not open thread.")
          )
        })
    },
    [axios]
  )

  useEffect(() => {
    if (!concierge) return undefined
    loadThreads()
    const intervalId = window.setInterval(() => {
      loadThreads({ silent: true })
    }, SMS_INBOX_LIST_POLL_MS)
    return () => window.clearInterval(intervalId)
  }, [concierge, loadThreads, profile?.active_account])

  useEffect(() => {
    if (threadFromUrl) {
      setSelectedId(threadFromUrl)
    }
  }, [threadFromUrl])

  useEffect(() => {
    selectedIdRef.current = selectedId
    if (!selectedId) {
      setThread(null)
      return undefined
    }
    loadThread(selectedId)
    const intervalId = window.setInterval(() => {
      loadThread(selectedId, { silent: true })
    }, SMS_INBOX_THREAD_POLL_MS)
    return () => window.clearInterval(intervalId)
  }, [loadThread, selectedId])

  function handleSync() {
    setIsSyncing(true)
    axios
      .post("/crm/api/concierge-sms/inbox/sync/")
      .then(({ data }) => {
        setMailbox(data.mailbox)
        setThreads(data.results || [])
        setError("")
        if (selectedId) {
          return loadThread(selectedId, { silent: true })
        }
        return undefined
      })
      .catch((err) => {
        setError(
          firstApiErrorMessage(err?.response?.data, "Could not sync SMS inbox.")
        )
      })
      .finally(() => setIsSyncing(false))
  }

  function handleReply(message, templateId) {
    const text = (message || "").trim()
    if (!selectedId || !text) return Promise.resolve(false)
    setIsReplying(true)
    return axios
      .post(`/crm/api/concierge-sms/inbox/threads/${selectedId}/reply/`, {
        message: text,
        template_id: templateId || null,
      })
      .then(({ data }) => {
        setThread(data)
        setThreads((current) => {
          const others = current.filter((row) => row.thread_id !== selectedId)
          return [data, ...others]
        })
        return true
      })
      .catch((err) => {
        setError(
          firstApiErrorMessage(err?.response?.data, "Could not send reply.")
        )
        return false
      })
      .finally(() => setIsReplying(false))
  }

  function handleStartThread(to, message, templateId) {
    setIsStarting(true)
    return axios
      .post("/crm/api/concierge-sms/inbox/threads/", {
        to,
        message,
        template_id: templateId || null,
      })
      .then(({ data }) => {
        setThreads((current) => {
          const others = current.filter(
            (row) => String(row.thread_id) !== String(data.thread_id)
          )
          return [data, ...others]
        })
        setThread(data)
        setSelectedId(String(data.thread_id))
        setNewTextOpen(false)
        setError("")
        return true
      })
      .catch((err) => {
        setError(
          firstApiErrorMessage(err?.response?.data, "Could not send the text.")
        )
        return false
      })
      .finally(() => setIsStarting(false))
  }

  useEffect(() => {
    const pane = threadScrollRef.current
    if (!pane || !thread?.messages?.length) return
    pane.scrollTop = pane.scrollHeight
  }, [thread?.thread_id, thread?.messages?.length])

  if (!concierge) {
    return null
  }

  const threadDetailsPath = textingOnly ? null : inboxContactDetailsPath(thread)
  const threadDisplayName =
    thread?.candidate_name ||
    thread?.contact_name ||
    thread?.counterparty_phone ||
    thread?.from_address

  return (
    <ProfileColumn
      sx={{
        gap: 1.25,
        width: "100%",
        minHeight: 0,
        pt: 0.5,
        paddingBlockStart: 1,
        height: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        maxHeight: {
          xs: "calc(100dvh - 148px)",
          md: `calc(100dvh - ${FOOTER_HEIGHT} - 200px)`,
        },
        overflow: "hidden",
      }}
    >
      <Row
        sx={{
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Column sx={{ gap: 0.25, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Messages
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "colors.gray600",
              display: { xs: "none", sm: "block" },
            }}
          >
            {textingOnly
              ? "Texts to and from your number. Start a new text or reply to a conversation."
              : "Read and reply to texts sent to your calling number for this parent account. Switch to Email for mailbox conversations."}
          </Typography>
        </Column>
        <Row sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {mailbox?.phone && (
            <Chip
              size="small"
              label={mailbox.phone}
              sx={{
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
                color: "colors.gray700",
                fontWeight: 500,
              }}
            />
          )}
          {mailbox?.last_sync_at && (
            <Typography variant="caption" sx={{ color: "colors.gray500" }}>
              {formatSyncLabel(mailbox.last_sync_at)}
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCommentOutlinedIcon />}
            onClick={() => setNewTextOpen(true)}
            disabled={!mailbox?.connected}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            New text
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={() => setTemplatesOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            SMS templates
          </Button>
          <LoadingButton
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlinedIcon />}
            loading={isSyncing}
            onClick={handleSync}
            disabled={!mailbox?.connected}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Sync
          </LoadingButton>
        </Row>
      </Row>
      {onChannelChange && (
        <InboxChannelToggle value={channel} onChange={onChannelChange} />
      )}

      {error && (
        <Alert severity="warning">
          {error}{" "}
          {!textingOnly && (
            <Link component={RouterLink} to="/settings?tab=phone_sms">
              Open Phone settings
            </Link>
          )}
        </Alert>
      )}
      {mailbox && !mailbox.connected && (
        <Alert severity="info">
          {textingOnly
            ? "Your texting number is not set up yet. Ask your admin to assign one."
            : "Buy a calling number in Phone settings to send and receive SMS from the same number you use for calls."}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          flex: "1 1 0%",
          minHeight: 0,
          height: "100%",
        }}
      >
        {showList && (
          <Column
            sx={{
              width: { xs: "100%", md: 380 },
              flexShrink: 0,
              minHeight: 0,
              height: { md: "100%" },
              borderRight: { md: "1px solid" },
              borderColor: { md: "colors.gray200" },
              bgcolor: "colors.gray50",
              overflow: "auto",
            }}
          >
            <Row
              sx={{
                px: 2.5,
                py: 1.75,
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "colors.gray200",
                bgcolor: "background.paper",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="text.primary"
              >
                Conversations
              </Typography>
              <Typography variant="caption" sx={{ color: "colors.gray500" }}>
                {threads.length}
              </Typography>
            </Row>
            {isLoading ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  gap: 1.5,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ color: "colors.gray500" }}>
                  Loading SMS…
                </Typography>
              </Column>
            ) : threads.length === 0 ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  py: 8,
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "colors.brand50",
                    color: "primary.main",
                  }}
                >
                  <SmsOutlinedIcon />
                </Box>
                <Typography fontWeight={600} color="text.primary">
                  No texts yet
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "colors.gray600" }}
                >
                  Inbound SMS to this account&apos;s calling number will appear
                  here. You can also text candidates from their profile.
                </Typography>
              </Column>
            ) : (
              threads.map((row) => (
                <ThreadRow
                  key={row.thread_id}
                  row={row}
                  selected={selectedId === row.thread_id}
                  onSelect={() => setSelectedId(row.thread_id)}
                />
              ))
            )}
          </Column>
        )}

        {showReadingPane && (
          <Column
            sx={{
              flex: "1 1 0%",
              minWidth: 0,
              minHeight: 0,
              height: { md: "100%" },
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {!thread ? (
              <Column
                sx={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "colors.gray100",
                    color: "colors.gray400",
                  }}
                >
                  <SmsOutlinedIcon fontSize="large" />
                </Box>
                <Typography fontWeight={600} color="text.primary">
                  Select a conversation
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "colors.gray600", maxWidth: 360 }}
                >
                  Choose a thread on the left to read inbound texts and reply
                  from the same number used for calls.
                </Typography>
              </Column>
            ) : (
              <Column
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <Column
                  sx={{
                    flexShrink: 0,
                    px: { xs: 2, md: 3 },
                    py: 1.25,
                    gap: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "colors.gray200",
                  }}
                >
                  <Row sx={{ alignItems: "flex-start", gap: 1 }}>
                    {isMobile && (
                      <IconButton
                        size="small"
                        onClick={() => setSelectedId(null)}
                        sx={{ mt: 0.25 }}
                        aria-label="Back to conversations"
                      >
                        <ArrowBackOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Column sx={{ gap: 0.75, minWidth: 0, flex: 1 }}>
                      {threadDetailsPath ? (
                        <Link
                          component={RouterLink}
                          to={threadDetailsPath}
                          underline="hover"
                          variant="subtitle1"
                          fontWeight={700}
                          color="text.primary"
                          sx={{ lineHeight: 1.3 }}
                        >
                          {threadDisplayName}
                        </Link>
                      ) : (
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color="text.primary"
                          sx={{ lineHeight: 1.3 }}
                        >
                          {threadDisplayName}
                        </Typography>
                      )}
                      <Row
                        sx={{
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <InboxContactChip
                          contact={thread}
                          fallbackLabel={thread.counterparty_phone}
                          unmatchedLabel="Unmatched number"
                        />
                        {thread.counterparty_phone ? (
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ color: "colors.gray600", fontWeight: 600 }}
                          >
                            {thread.counterparty_phone}
                          </Typography>
                        ) : null}
                      </Row>
                    </Column>
                  </Row>
                </Column>

                <Column
                  ref={threadScrollRef}
                  sx={{
                    flex: "1 1 0%",
                    minHeight: 0,
                    overflowX: "hidden",
                    overflowY: "auto",
                    px: { xs: 2, md: 2.5 },
                    py: 1.5,
                    gap: 1.5,
                    bgcolor: "colors.dimBackground",
                  }}
                >
                  {(thread.messages || []).map((message) => (
                    <SmsMessageCard key={message.id} message={message} />
                  ))}
                </Column>

                <Column
                  sx={{
                    flexShrink: 0,
                    zIndex: 2,
                    gap: 1,
                    px: { xs: 2, md: 2.5 },
                    py: 1.25,
                    borderTop: "1px solid",
                    borderColor: "colors.gray200",
                    bgcolor: "background.paper",
                  }}
                >
                  <SmsInboxReplyComposer
                    key={`${thread.thread_id}-${templatesRevision}`}
                    thread={thread}
                    mailboxConnected={Boolean(mailbox?.connected)}
                    isReplying={isReplying}
                    templatesRevision={templatesRevision}
                    onManageTemplates={() => setTemplatesOpen(true)}
                    onSend={handleReply}
                  />
                </Column>
              </Column>
            )}
          </Column>
        )}
      </Paper>
      <SmsNewTextDialog
        open={newTextOpen}
        fromPhone={mailbox?.phone}
        isSending={isStarting}
        onClose={() => setNewTextOpen(false)}
        onManageTemplates={() => setTemplatesOpen(true)}
        onSend={handleStartThread}
      />
      <SmsInboxTemplatesDrawer
        open={templatesOpen}
        onClose={() => {
          setTemplatesOpen(false)
          setTemplatesRevision((current) => current + 1)
        }}
      />
    </ProfileColumn>
  )
}

function ThreadRow({ row, selected, onSelect }) {
  const unread = row.unread_count > 0
  const name =
    row.candidate_name ||
    row.contact_name ||
    row.counterparty_phone ||
    row.from_address
  const phone = row.candidate_phone || row.counterparty_phone
  const showPhone =
    Boolean(phone) &&
    String(phone).toLowerCase() !== String(name || "").toLowerCase()

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      sx={{
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderBottom: "1px solid",
        borderColor: "colors.gray200",
        bgcolor: selected ? "colors.brand50" : "background.paper",
        borderLeft: "3px solid",
        borderLeftColor: selected
          ? "primary.main"
          : unread
          ? "primary.light"
          : "transparent",
        "&.Mui-selected": {
          bgcolor: "colors.brand50",
        },
        "&.Mui-selected:hover": {
          bgcolor: "colors.brand100",
        },
        "&:hover": {
          bgcolor: selected ? "colors.brand100" : "colors.gray50",
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          mt: 0.25,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: unread ? "primary.main" : "colors.gray200",
          color: unread ? "common.white" : "colors.gray700",
        }}
      >
        {initials(name, phone)}
      </Avatar>
      <Column sx={{ minWidth: 0, flex: 1, gap: 0.35 }}>
        <Row sx={{ alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: showPhone ? "0 1 auto" : 1,
              maxWidth: showPhone ? "46%" : "100%",
              minWidth: 0,
              fontWeight: unread ? 700 : 600,
              color: "text.primary",
            }}
          >
            {name}
          </Typography>
          {showPhone ? (
            <Typography
              variant="caption"
              noWrap
              title={phone}
              sx={{
                flex: 1,
                minWidth: 0,
                color: unread ? "primary.main" : "colors.gray600",
                fontWeight: unread ? 600 : 500,
              }}
            >
              {phone}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              color: unread ? "primary.main" : "colors.gray500",
              fontWeight: unread ? 700 : 500,
            }}
          >
            {formatListTime(row.latest_at)}
          </Typography>
        </Row>
        {row.snippet ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: "colors.gray500", display: "block" }}
          >
            {row.snippet}
          </Typography>
        ) : null}
      </Column>
      {unread ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            mt: 1,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
          }}
        />
      ) : null}
    </ListItemButton>
  )
}
